import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { volumes, weeks } from '../data/content';
import { volumeCovers, volumesFanUrl } from '../data/images';
import { CtaButton } from './CtaButton';
import { ImageSlot } from './ImageSlot';
import { SectionHeading } from './SectionHeading';

/** Cell colors of the 52-week grid (full class names so Tailwind keeps them) */
const cell = {
  key: 'bg-sun text-ink',
  class: 'bg-brand text-white',
  check: 'bg-leaf text-white'
} as const;

/** New in v2: the 5 volumes (fan mockup + 52-week grid + one tab per volume with its classes). Replaces the
 *  52-week plan and the list of 10 classes of v1. */
export function VolumesSection() {
  const [active, setActive] = useState(0);
  const item = volumes.items[active];
  const list = weeks.filter(w => w.vol === active);
  const quarters = [0, 1, 2, 3].map(q => weeks.filter(w => Math.ceil(w.week / 13) === q + 1));
  const pick = (vol: number) => {
    setActive(vol);
    document.getElementById('volume-panel')?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  };
  return <section id="volumes" aria-labelledby="volumes-title" className="scroll-mt-4 bg-mist py-12 md:py-20" data-id="element-258" data-mp="2edtpf6r3akok">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-259" data-mp="2j6d09vye71po">
        <SectionHeading id="volumes-title" eyebrow={volumes.eyebrow} title={volumes.title} sub={volumes.sub} data-id="element-260" data-mp="2o4esp7hpkddt" />

        {/* The 5 volumes as books. Fixed ratio box so the scroll to #offer never lands short when it loads. */}
        <div className="mx-auto mt-6 aspect-[1600/933] w-full max-w-[860px] md:mt-8" data-id="element-261" data-mp="1vexsvorrdu7k">
          <ImageSlot url={volumesFanUrl} alt={volumes.fanAlt} slotName="volumesFanUrl" fit="contain" data-id="element-262" data-mp="20f2eft7ixf4n" />
        </div>

        {/* 52 weeks · 52 classes: every square is a complete class */}
        <div className="mx-auto mt-6 max-w-[860px] rounded-2xl border border-mist-line bg-white p-4 md:mt-8 md:p-6" data-id="element-263" data-mp="3btzb9p9y98mk">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-264" data-mp="3fv1d8dv93ys8">
            <h3 className="font-display text-[14px] font-black tracking-[0.1em] text-brand md:text-[16px]" data-id="element-265" data-mp="1gqknldk6xcsh">{volumes.gridTitle}</h3>
            <p className="text-[13px] text-ink/60" data-id="element-266" data-mp="1bqy6gt18rcee">{volumes.gridHint}</p>
          </div>
          <div className="mt-3 space-y-1.5" data-id="element-267" data-mp="1pqg0pnxb3hv5">
            {quarters.map((q, qi) => <div key={qi} className="flex items-center gap-1.5" data-id="element-268" data-mp="1krwa3a7rphwg" data-mpi={qi}>
                <span className="w-6 shrink-0 font-display text-[11px] font-black text-ink/60 md:w-8 md:text-[13px]" data-id="element-269" data-mp="1kqlbrpx3d2cq">Q{qi + 1}</span>
                <div className="grid flex-1 gap-[3px] md:gap-1.5" style={{
              gridTemplateColumns: 'repeat(13, minmax(0, 1fr))'
            }} data-id="element-270" data-mp="t21bemy0sfv4">
                  {q.map(w => <button key={w.week} type="button" onClick={() => pick(w.vol)} aria-label={`Week ${w.week}: ${w.title}`} title={`Week ${w.week}: ${w.title}`} className={`flex aspect-square items-center justify-center rounded-[4px] font-display text-[9px] font-extrabold tabular-nums transition-transform duration-150 ease-out hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-coral sm:text-[11px] md:rounded-md md:text-[13px] ${cell[w.kind]} ${w.vol === active ? 'ring-2 ring-coral ring-offset-1' : ''}`} data-id="element-271" data-mp="16uhgdtahenyo">
                      {w.week}
                    </button>)}
                </div>
              </div>)}
          </div>
          <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[12px] font-medium text-ink/75 md:text-[13px]" data-id="element-272" data-mp="2uqxhds387eqq">
            <li className="flex items-center gap-1.5" data-id="element-273" data-mp="2zofs9o0t041d">
              <span className="h-3 w-3 rounded-[3px] bg-sun" aria-hidden="true" data-id="element-274" data-mp="2zrbn33hcucll" />
              {volumes.legend.key}
            </li>
            <li className="flex items-center gap-1.5" data-id="element-275" data-mp="1oalq8aytmx4n">
              <span className="h-3 w-3 rounded-[3px] bg-brand" aria-hidden="true" data-id="element-276" data-mp="1o7z7df6hcv74" />
              {volumes.legend.class}
            </li>
            <li className="flex items-center gap-1.5" data-id="element-277" data-mp="1ysqonii4hv9w">
              <span className="h-3 w-3 rounded-[3px] bg-leaf" aria-hidden="true" data-id="element-278" data-mp="1yr5sjog5u49x" />
              {volumes.legend.check}
            </li>
          </ul>
        </div>

        {/* One tab per volume (its real cover) */}
        <div className="mx-auto mt-6 max-w-[860px] md:mt-8" data-id="element-279" data-mp="2miunzdoa38v3">
          <div role="tablist" aria-label={volumes.tablistLabel} className="grid grid-cols-5 gap-2 md:gap-4" data-id="element-280" data-mp="2mib6tz6gcoi4">
            {volumes.items.map((v, i) => {
            const on = i === active;
            return <button key={v.tab} role="tab" type="button" aria-selected={on} aria-controls="volume-panel" onClick={() => setActive(i)} className="group flex flex-col items-center gap-1.5 focus-visible:outline-none" data-id="element-281" data-mp="2dk985qa6y7y0" data-mpi={i}>
                  <span className={`relative block aspect-[9/16] w-full overflow-hidden rounded-md shadow-[0_10px_22px_-12px_rgba(20,23,28,0.7)] transition-[transform,box-shadow] duration-150 ease-out group-focus-visible:ring-2 group-focus-visible:ring-brand ${on ? '-translate-y-1 ring-[3px] ring-coral' : 'opacity-80 group-hover:opacity-100'}`} data-id="element-282" data-mp="n114yyefa9rr">
                    <ImageSlot url={volumeCovers[i]} alt={`${v.label}: ${v.title}`} slotName={`volumeCovers[${i}]`} fit="cover" data-id="element-283" data-mp="15sk42u5bt3oi" />
                  </span>
                  <span className={`font-display text-[11px] font-black tracking-[0.06em] md:text-[13px] ${on ? 'text-coral-ink' : 'text-ink/60'}`} data-id="element-284" data-mp="1fodp71x425hw">{v.tab}</span>
                </button>;
          })}
          </div>

          <div id="volume-panel" role="tabpanel" className="mt-4 scroll-mt-20 overflow-hidden rounded-2xl border border-mist-line bg-white" data-id="element-285" data-mp="3hhl5oeogmt5d">
            <div className="bg-brand p-5 md:p-7" data-id="element-286" data-mp="3ee5gs1e7p758">
              <p className="font-display text-[11px] font-extrabold tracking-[0.14em] text-sun md:text-[12px]" data-id="element-287" data-mp="3eexcrdbq16w1">{item.label}</p>
              <h3 className="mt-2 font-display text-[24px] font-black uppercase leading-tight tracking-tight text-white md:text-[30px]" data-id="element-288" data-mp="39hotf9eu6wt7">{item.title}</h3>
              <p className="mt-1 font-display text-[13px] font-bold text-sun md:text-[14px]" data-id="element-289" data-mp="3jcgp27ouz9lc">{item.meta}</p>
              <p className="mt-3 text-[16px] leading-relaxed text-white/85" data-id="element-290" data-mp="16kj22mm72ap">{item.text}</p>
            </div>

            <div className="grid gap-6 p-4 md:grid-cols-[1.3fr_1fr] md:p-7" data-id="element-291" data-mp="3sdna8u2gd8kx">
              <div data-id="element-292" data-mp="awnc22tqkg91">
                <h4 className="font-display text-[12px] font-black tracking-[0.12em] text-brand" data-id="element-293" data-mp="ax5i1r67a8u5">{volumes.classesLabel}</h4>
                <ol className="mt-2 divide-y divide-hair" data-id="element-294" data-mp="60y71jyy675c">
                  {list.map(w => <li key={w.week} className="flex items-center gap-3 py-2" data-id="element-295" data-mp="jw0maxpop8ut">
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-display text-[12px] font-black tabular-nums ${cell[w.kind]}`} data-id="element-296" data-mp="fymg7exavh71">
                        {w.week}
                      </span>
                      <p className="min-w-0 flex-1 font-display text-[14px] font-extrabold leading-snug text-ink md:text-[15px]" data-id="element-297" data-mp="pkduswf5qm88">{w.title}</p>
                      <span className="shrink-0 text-right text-[12px] font-medium text-ink/55 md:text-[13px]" data-id="element-298" data-mp="uifglp3ywzne">{w.value}</span>
                    </li>)}
                </ol>
              </div>
              <div data-id="element-299" data-mp="km2jeh97s3nw">
                <h4 className="font-display text-[12px] font-black tracking-[0.12em] text-brand" data-id="element-300" data-mp="kndpwou1npip">{volumes.includesLabel}</h4>
                <ul className="mt-3 space-y-2.5" data-id="element-301" data-mp="avhgwiqf6ezp">
                  {item.includes.map(line => <li key={line} className="flex gap-2.5 text-[15px] leading-snug text-ink" data-id="element-302" data-mp="3t14a61wmqbml">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-leaf text-white" data-id="element-303" data-mp="1ut9w7zg1ewb">
                        <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-304" data-mp="hd2mgyw6kvom" />
                      </span>
                      {line}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-8 flex max-w-[460px] justify-center md:mt-10" data-id="element-305" data-mp="3dbdxdofo7i0">
          <CtaButton label={volumes.cta} microcopy={volumes.ctaMicrocopy} align="center" data-id="element-306" data-mp="3ujcj55lpw6vq" />
        </div>
      </div>
    </section>;
}